/**
 * Nest design tokens: the single source for colour, type, radius and spacing across every app.
 *
 * Brand system "Nest violet" (decisions A-13, A-24): a violet brand scale on cool slate neutrals, fixed status
 * colours, product colours, per-app accents and a colour-blind-distinct chart palette. The console is light
 * only (A-5); the light palette below references these scales. The dark palette (Pay Console prototype
 * verbatim) is kept but unused.
 */

/** Brand violet, 50 (lightest) to 900. 600 is the brand colour: logo, primary buttons, links, focus. */
export const brand = {
  50: '#F5F3FF', 100: '#EEEBFF', 200: '#DCD6FF', 300: '#BEB4FB', 400: '#9C8FF4',
  500: '#7A6AEC', 600: '#5B4BE0', 700: '#4B3BD0', 800: '#3B2EA8', 900: '#2A2179',
} as const;

/** Cool slate neutrals, 0 (white) to 900 (ink): text, borders, rail, page, cards. */
export const neutral = {
  0: '#FFFFFF', 25: '#F7F8FB', 50: '#F5F6FA', 75: '#F3F4F8', 100: '#EEF0F6', 125: '#EDEFF4', 150: '#E4E7EF',
  175: '#E1E4EC', 200: '#D6DAE4', 300: '#B4BAC8', 500: '#5F6880', 600: '#4E576B', 900: '#141826',
} as const;

/** Status colours: identical in every app and never re-themed (text on tint passes WCAG AA). */
export const status = {
  success: '#0B8A68', warning: '#A45F00', danger: '#C9333F', info: '#2563C9', dangerSolid: '#C2414B',
} as const;

/**
 * Chart palette: 8 hues ordered for maximum contrast between neighbours and distinguishable with common
 * colour-vision deficiencies. Use in order; never use status colours for data series.
 */
export const chart = ['#5B4BE0', '#0E9F8E', '#D98E04', '#C2417F', '#2F7FD6', '#4C9A2A', '#E0632B', '#6B7489'] as const;

/** Per-app accent (active nav, primary buttons, focus inside that app). Payments uses the brand violet. */
export const appAccent = {
  payments: brand[600], org: '#2563C9', ops: '#0E8A7B', account: '#475467',
  smartbill: '#0B7F8A', retail: '#4F46E5', giving: '#A45F00', stay: '#B8327F',
} as const;

export type Mode = 'light' | 'dark';

export interface Palette {
  bg: string;
  rail: string;
  railBorder: string;
  /** Rail item hover and the lifted active tab (tinted-rail style). */
  railHover: string;
  railActive: string;
  railActiveShadow: string;
  panel: string;
  sunk: string;
  popover: string;
  border: string;
  border2: string;
  divider: string;
  text: string;
  dim: string;
  caption: string;
  faint: string;
  accent: string;
  accentText: string;
  button: string;
  buttonRing: string;
  success: string;
  warning: string;
  danger: string;
  info: string;
  dangerSolid: string;
  staff: string;
}

export const palettes: Record<Mode, Palette> = {
  dark: {
    bg: '#0A0C12',
    rail: '#0C0F17',
    railBorder: '#161B26',
    railHover: '#141826',
    railActive: '#161B29',
    railActiveShadow: '0 0 0 1px #262D3E, 0 1px 2px rgba(0,0,0,0.4)',
    panel: '#121623',
    sunk: '#0F131C',
    popover: '#161A28',
    border: '#1E2433',
    border2: '#262D3E',
    divider: '#171C29',
    text: '#ECEEF5',
    dim: '#9AA3B7',
    caption: '#808AA1',
    faint: '#39414F',
    accent: '#7C6BFF',
    accentText: '#A99CFF',
    button: '#6A58F0',
    buttonRing: '#8E7FFF',
    success: '#2FD6A6',
    warning: '#F5B14C',
    danger: '#FF8089',
    info: '#6EA8FF',
    dangerSolid: '#C2414B',
    staff: '#F5B14C',
  },
  light: {
    bg: neutral[50],
    rail: neutral[100],
    railBorder: neutral[175],
    railHover: neutral[150],
    railActive: neutral[0],
    railActiveShadow: `0 0 0 1px ${neutral[175]}, 0 1px 2px rgba(20,24,38,0.08)`,
    panel: neutral[0],
    sunk: neutral[75],
    popover: neutral[0],
    border: neutral[150],
    border2: neutral[200],
    divider: neutral[125],
    text: neutral[900],
    dim: neutral[600],
    caption: neutral[500],
    faint: neutral[300],
    accent: brand[600],
    accentText: brand[700],
    button: brand[600],
    buttonRing: brand[600],
    success: status.success,
    warning: status.warning,
    danger: status.danger,
    info: status.info,
    dangerSolid: status.dangerSolid,
    staff: status.warning,
  },
};

export type Product = 'SmartBill' | 'Retail' | 'Giving' | 'Stay';

export const productColors: Record<Mode, Record<Product, string>> = {
  dark: { SmartBill: '#37C9D6', Retail: '#A99CFF', Giving: '#F5B14C', Stay: '#FF7AC8' },
  light: { SmartBill: '#0B7F8A', Retail: '#5B4BE0', Giving: '#A45F00', Stay: '#B8327F' },
};

export type Tone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

/** Status vocabulary shared by every console (Pay Console ui.py STATUS_COLORS + Org/Ops states). */
export const statusTone: Record<string, Tone> = {
  Succeeded: 'success', Captured: 'success', Paid: 'success', Settled: 'success', Matched: 'success', Won: 'success',
  Ready: 'success', Live: 'success', Healthy: 'success', Active: 'success', Delivered: 'success', Resolved: 'success',
  Approved: 'success', Completed: 'success', Verified: 'success', Enabled: 'success', Running: 'info',
  Authorized: 'info', 'In transit': 'info', 'Under review': 'info', Scheduled: 'info', Pending: 'info',
  Waiting: 'info', Invited: 'info', 'In progress': 'info', Provisioning: 'info', Queued: 'info',
  'Partially refunded': 'warning', 'Needs response': 'warning', Degraded: 'warning', Exception: 'warning',
  Inquiry: 'warning', Expiring: 'warning', Retrying: 'warning', 'Pending approval': 'warning', Open: 'warning',
  'Needs attention': 'warning', 'Action needed': 'warning', Suspended: 'warning', Draft: 'warning',
  Failed: 'danger', Declined: 'danger', Lost: 'danger', Disputed: 'danger', Returned: 'danger',
  Unmatched: 'danger', Rejected: 'danger', Escalated: 'danger', Down: 'danger', Locked: 'danger', Error: 'danger',
  Refunded: 'neutral', Voided: 'neutral', Canceled: 'neutral', Closed: 'neutral', Accepted: 'neutral',
  Revoked: 'neutral', Expired: 'neutral', Disabled: 'neutral', Inactive: 'neutral', Estimated: 'neutral',
};

export const fonts = {
  sans: "'Space Grotesk', system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace",
};

export const radius = { card: 16, tile: 12, dialog: 18, menu: 11, input: 10, button: 9, select: 8, badge: 6, chip: 4, pill: 20 };

export const layout = { railWidth: 248, topBar: 60, contentPadX: 28, contentPadY: 26, gap: 14 };

/** rgba() from a hex colour, used for tinted pills, rings and tiles. */
export function alpha(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}
